import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import { useTheme } from '../ThemeContext';
import { useT } from '../i18n';
import { useContent } from '../i18n/useContent';
import './ThemeSwitcher.css';

/**
 * The header control for switching visual modes.
 *
 * A menu rather than a dialog: it is small and transient, so it deliberately
 * does not take a body scroll lock the way the palette and project modal do.
 *
 * Labels and hints come from useContent(), not the raw THEMES import, so a
 * theme's name follows the reader's language the same way project and
 * workflow copy does.
 */
function ThemeSwitcher() {
  const { theme, setTheme } = useTheme();
  const { themes } = useContent();
  const t = useT();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);

  // Named to avoid shadowing the `t` translate function above.
  const active = themes.find((entry) => entry.id === theme) || themes[0];
  const activeIndex = themes.findIndex((entry) => entry.id === theme);

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus && triggerRef.current) triggerRef.current.focus();
  }, []);

  // Focus the active row when the menu opens, so the keyboard lands somewhere
  // meaningful rather than on the first item every time.
  useEffect(() => {
    if (!open) return;
    const target = itemRefs.current[activeIndex >= 0 ? activeIndex : 0];
    if (target) target.focus();
  }, [open, activeIndex]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        close(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, close]);

  const onMenuKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();
    const current = itemRefs.current.indexOf(document.activeElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    const next = (current + step + themes.length) % themes.length;
    const target = itemRefs.current[next];
    if (target) target.focus();
  };

  return (
    <div className="theme-switcher" ref={wrapperRef}>
      <button
        ref={triggerRef}
        type="button"
        className="theme-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('a11y.theme', { label: active.label })}
        onClick={() => setOpen((isOpen) => !isOpen)}
      >
        <span className="theme-chips" aria-hidden="true">
          {active.swatch.map((colour, index) => (
            <span key={index} className="theme-chip" style={{ background: colour }} />
          ))}
        </span>
        <span className="theme-trigger-text">{active.label}</span>
      </button>

      {open && (
        <div
          className="theme-menu"
          role="menu"
          aria-label={t('theme.appearance')}
          onKeyDown={onMenuKeyDown}
        >
          <p className="theme-menu-title">{t('theme.appearance')}</p>
          {[
            { key: 'colour', label: t('theme.colour') },
            { key: 'layout', label: t('theme.layout') },
          ].map((group) => (
            <div className="theme-group" key={group.key}>
              <p className="theme-group-label" aria-hidden="true">{group.label}</p>
              {themes
                .map((option, flatIndex) => ({ option, flatIndex }))
                .filter(({ option }) => option.group === group.key)
                .map(({ option, flatIndex }) => (
                  <button
                    key={option.id}
                    ref={(node) => { itemRefs.current[flatIndex] = node; }}
                    type="button"
                    role="menuitemradio"
                    aria-checked={option.id === theme}
                    className={`theme-option ${option.id === theme ? 'is-active' : ''}`}
                    onClick={() => {
                      setTheme(option.id);
                      close();
                    }}
                  >
                    <span className="theme-chips" aria-hidden="true">
                      {option.swatch.map((colour, i) => (
                        <span key={i} className="theme-chip" style={{ background: colour }} />
                      ))}
                    </span>
                    <span className="theme-option-text">
                      <span className="theme-option-label">{option.label}</span>
                      <span className="theme-option-hint">{option.hint}</span>
                    </span>
                    <span className="theme-option-check" aria-hidden="true">
                      {option.id === theme ? <FaCheck /> : null}
                    </span>
                  </button>
                ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ThemeSwitcher;
